import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronDown } from '@ng-icons/lucide';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { formatCurrencyWithSymbol } from '../../utils/currency.utils';
import { VoucherRedemption } from '../../services/voucher.service';
import { parseApiDate } from '../../utils/period.utils';

/** "Details & Verlauf" under a voucher on /redeem and /verify: dates and past redemptions. */
@Component({
  selector: 'app-voucher-timeline',
  standalone: true,
  imports: [NgIcon, HlmCardImports, HlmTableImports, HlmTypographyImports],
  providers: [provideIcons({ lucideChevronDown })],
  templateUrl: './voucher-timeline.component.html',
  styleUrls: ['./voucher-timeline.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class VoucherTimelineComponent {
  readonly createdAt = input.required<string>();
  readonly expiresAt = input<string | null>(null);
  readonly redemptions = input<VoucherRedemption[]>([]);

  protected readonly money = formatCurrencyWithSymbol;

  /** Newest first. */
  protected readonly sorted = computed(() =>
    [...this.redemptions()].sort((a, b) => b.redeemed_at.localeCompare(a.redeemed_at)),
  );
  /** Network vouchers are redeemed at different members: say where. */
  protected readonly showLocation = computed(() =>
    this.redemptions().some((r) => !!r.redeeming_company_name),
  );
  protected readonly isExpired = computed(() => {
    const expiresAt = this.expiresAt();
    return !!expiresAt && parseApiDate(expiresAt) < new Date();
  });

  protected formatDate(value: string): string {
    return parseApiDate(value).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  protected formatDateTime(value: string): string {
    return parseApiDate(value).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
